import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatSocket } from '../../hooks/useChatSocket';
import {
  MessageSquare,
  BarChart2,
  Loader2,
  Copy,
  Download,
  RefreshCw,
  Mail,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  Edit,
  Share2,
  Info,
  ExternalLink,
  ShieldOff,
} from 'lucide-react';
import { useSimulationSocket } from '@/hooks/useSimulationSocket';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog.jsx';
import { CustomDialogClose } from '../../components/ui/dialog-custom.jsx';
import html2canvas from 'html2canvas';
import { validateBase64Image, createPlaceholderDataUrl, compressBase64Image } from '../../utils/imageValidation';

// Import new sub-components
import ExportModal from './components/ExportModal';
import ChatInterface from './components/ChatInterface';
import SimulationAnalysisPanel from './components/SimulationAnalysisPanel';
import EditRerunModal from './components/EditRerunModal';
import ShareModal from './ShareModal';
import { zIndexClasses } from '../../config/zIndex';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Persona {
  id: number;
  name: string;
  data: any;
}

interface SimulationData {
  id: number;
  audience_id: number;
  simulation_response: string;
  source_model: string;
  optimization_response: string;
  status: string;
  personas: Persona[];
  created_at: string;
  updated_at: string;
  content?: string;
  segments?: {
    id: number;
    name: string;
  }[];
  num_tabs?: number;
}

interface ContentData {
  [key: string]: unknown;
  audience_name?: string;
  name?: string;
  goal?: string;
  task?: string;
  content_type?: string;
  segment_ids?: number[];
  persona_filters?: Record<string, Record<string, unknown>>;
  images?: string[];
  current_logo?: string;
  questions?: any[];
  num_questions?: number;
}

interface SimulationResultsContentProps {
  simulationId: string | number;
  onError?: (error: string) => void;
  defaultChatOpen?: boolean;
  isPublicView?: boolean;
  isAdminView?: boolean;
}

// Helper function at the top level
function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const SimulationResultsContent: React.FC<SimulationResultsContentProps> = React.memo(({
  simulationId,
  onError,
  defaultChatOpen = false,
  isPublicView = false,
  isAdminView = false
}): React.ReactElement => {
  const [simulation, setSimulation] = useState<SimulationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'simulation' | 'advanced'>('simulation');
  const [chatTab, setChatTab] = useState<'simulation' | 'persona'>('simulation');
  const [selectedPersona, setSelectedPersona] = useState<number | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [isChatCollapsed, setIsChatCollapsed] = useState(!defaultChatOpen);
  const [showHoverMessage, setShowHoverMessage] = useState(false);
  const [simulationStatus, setSimulationStatus] = useState<'pending' | 'running' | 'completed' | 'partial'>('pending');
  const [currentStep, setCurrentStep] = useState<string>('Initializing simulation...');
  const [showTooltip, setShowTooltip] = useState('');
  const [copied, setCopied] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'success' | 'error'>('idle');
  const [exportError, setExportError] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportEmailStatus, setExportEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [exportEmailError, setExportEmailError] = useState('');
  const [rerunLoading, setRerunLoading] = useState(false);
  const [rerunError, setRerunError] = useState('');
  const navigate = useNavigate();

  // State for collapsible cards in content summary
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({
    details: true,
    audience: false,
    additional: false,
    personas: false,
    analysis: false,
    questions: true
  });

  // State for dropdown
  const [isDetailsDropdownOpen, setIsDetailsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  
  // State for image modal
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);
  
  // State for export preparation
  const [isPreparingExport, setIsPreparingExport] = useState(false);

  // PDF generation state
  const [cachedPdfUrl, setCachedPdfUrl] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfCacheKey, setPdfCacheKey] = useState<string | null>(null);
  const [pdfCheckInterval, setPdfCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [currentEmailRecipient, setCurrentEmailRecipient] = useState<string>('');

  // Media upload states
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Media viewer modal states
  const [viewerMedia, setViewerMedia] = useState<{type: 'image' | 'video', src: string} | null>(null);
  
  // Upload progress state
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  // Add state for email notification modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailNotificationStatus, setEmailNotificationStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [emailNotificationError, setEmailNotificationError] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');

  // State for Edit & Rerun modal
  const [showEditRerunModal, setShowEditRerunModal] = useState(false);

  // State for sharing
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isPublicShared, setIsPublicShared] = useState(false);
  const [isUnsharing, setIsUnsharing] = useState(false);
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const shareDropdownRef = useRef<HTMLDivElement>(null);

  // State for estimated time display
  const [estimatedTime, setEstimatedTime] = useState<{
    baseMinutes: number;
    queueMinutes: number;
    totalMinutes: number;
    queuedCount: number;
  } | null>(null);
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null);

  // State for notifications and confirmations
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{message: string, onConfirm: () => void} | null>(null);

  // WebSocket chat functionality
  const { 
    chatState, 
    sendMessage: sendChatMessage, 
    addOptimisticMessage,
    removeOptimisticMessage,
    clearChat,
    isConnected: isChatConnected 
  } = useChatSocket({
    autoConnect: true,
    chatType: chatTab,
    simulationId: simulationId ? parseInt(String(simulationId)) : undefined,
    personaId: selectedPersona || undefined,
    chatId: `content_${chatTab}_${String(simulationId)}_${selectedPersona || 'sim'}`,
    onMessageReceived: (data) => {},
    onResponseStarted: (data) => {},
    onResponseCompleted: (data) => {},
    onResponseFailed: (data) => {
      console.error('Chat response failed:', data);
      setError(data.error || data.detail || 'Chat response failed');
    }
  });

  // WebSocket integration for real-time simulation updates
  const shouldConnectWebSocket = simulation?.simulation_response === null || simulation?.simulation_response === undefined;
  const { isConnected } = useSimulationSocket({
    onSimulationCompleted: (data) => {
      if (data.simulation_id === Number(simulationId)) {
        fetchSimulationStatus();
      }
    },
    onSimulationFailed: (data) => {
      if (data.simulation_id === Number(simulationId)) {
        fetchSimulationStatus();
      }
    },
    autoConnect: shouldConnectWebSocket
  });

  // Memoized callback functions
  const handleToggleDetailsDropdown = useCallback(() => {
    setIsDetailsDropdownOpen(prev => !prev);
  }, []);
  
  const handleOpenExportModal = useCallback(() => {
    setShowExportModal(true);
  }, []);
  
  const handleCloseExportModal = useCallback(() => {
    setShowExportModal(false);
    setExportStatus('idle');
    setExportError('');
    setExportEmailStatus('idle');
    setExportEmailError('');
    setCurrentEmailRecipient('');
  }, []);
  
  const handleImageClick = useCallback((src: string, alt: string) => {
    setSelectedImage({ src, alt });
  }, []);
  
  const handleCloseImageModal = useCallback(() => {
    setSelectedImage(null);
  }, []);
  
  const handleViewMedia = useCallback((type: 'image' | 'video', src: string) => {
    setViewerMedia({ type, src });
  }, []);
  
  const handleCloseViewerMedia = useCallback(() => {
    setViewerMedia(null);
  }, []);
  
  const handleToggleChat = useCallback(() => {
    setIsChatCollapsed(prev => !prev);
  }, []);
  
  const handleRemoveMedia = useCallback((index: number) => {
    if (index === -1) {
      // Clear all
      mediaPreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setMediaFiles([]);
      setMediaPreviewUrls([]);
    } else {
      setMediaFiles(prev => prev.filter((_, i) => i !== index));
      setMediaPreviewUrls(prev => prev.filter((_, i) => i !== index));
    }
  }, [mediaPreviewUrls]);
  
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  
  const handleChatTabChange = useCallback((tab: 'simulation' | 'persona') => {
    setChatTab(tab);
  }, []);
  
  const handleActiveTabChange = useCallback((tab: 'simulation' | 'advanced') => {
    setActiveTab(tab);
  }, []);
  
  const handleCopyToClipboard = useCallback(async () => {
    if (!simulation?.simulation_response) return;
    await navigator.clipboard.writeText(simulation.simulation_response);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }, [simulation]);
  
  const handleRerunSimulation = useCallback(async () => {
    if (!simulation?.id) return;
    setRerunLoading(true);
    setRerunError('');
    try {
      const res = await fetch(`${API_URL}/simulations/${simulation.id}/rerun`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: AbortSignal.timeout(300000)
      });
      if (!res.ok) throw new Error('Failed to rerun simulation');
      const data = await res.json();
      if (!data.simulation_id) throw new Error('No new simulation id returned');
      navigate(`/simulation-results/${data.simulation_id}`);
    } catch (err: any) {
      setRerunError(err?.message || 'Failed to rerun simulation');
    } finally {
      setRerunLoading(false);
    }
  }, [simulation, navigate]);

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const response = await fetch(`${API_URL}/simulations/${simulationId}/share`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to generate share link');

      const data = await response.json();
      setShareUrl(data.share_url);
      setIsPublicShared(true);
      setIsShareModalOpen(true);
    } catch (err) {
      console.error('Error sharing simulation:', err);
      setNotification({message: 'Failed to generate share link', type: 'error'});
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setIsSharing(false);
    }
  };

  const handleUnshare = async () => {
    setConfirmDialog({
      message: 'Make this simulation private? The share link will stop working.',
      onConfirm: async () => {
        setConfirmDialog(null);
        setIsUnsharing(true);
        try {
          const response = await fetch(`${API_URL}/simulations/${simulationId}/share`, {
            method: 'DELETE',
            credentials: 'include'
          });
          if (!response.ok) throw new Error('Failed to unshare simulation');

          setIsPublicShared(false);
          setShareUrl(null);
          setNotification({message: 'Simulation is now private', type: 'success'});
          setTimeout(() => setNotification(null), 3000);
        } catch (err) {
          console.error('Error unsharing simulation:', err);
          setNotification({message: 'Failed to make simulation private', type: 'error'});
          setTimeout(() => setNotification(null), 3000);
        } finally {
          setIsUnsharing(false);
        }
      }
    });
  };

  const toggleCard = useCallback((cardName: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  }, []);

  useEffect(() => {
    // Reset state when simulationId changes
    setSimulation(null);
    setLoading(true);
    setError(null);
    clearChat();
    setSelectedPersona(null);
    setActiveTab('simulation');
    setChatTab('simulation');
    setMediaFiles([]);
    setMediaPreviewUrls([]);
    setIsUploadingMedia(false);

    if (!simulationId) {
      const errorMsg = 'No simulation ID provided';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      setLoading(false);
      return;
    }

    fetchSimulationStatus(true);
  }, [simulationId]);

  // Clean up media preview URLs on unmount
  useEffect(() => {
    return () => {
      mediaPreviewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [mediaPreviewUrls]);
  
  // Reset upload state when typing starts
  useEffect(() => {
    if (chatState.isTyping || chatState.isSending) {
      const timer = setTimeout(() => {
        setIsUploadingMedia(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [chatState.isTyping, chatState.isSending]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (pdfCheckInterval) {
        clearInterval(pdfCheckInterval);
      }
    };
  }, [pdfCheckInterval]);

  // Clear PDF cache when simulation changes
  useEffect(() => {
    setCachedPdfUrl(null);
    setPdfCacheKey(null);
    setPdfGenerating(false);
    
    if (pdfCheckInterval) {
      clearInterval(pdfCheckInterval);
      setPdfCheckInterval(null);
    }
  }, [simulationId]);

  // Fetch queue status and calculate estimated time when simulation starts running
  useEffect(() => {
    if ((simulationStatus === 'running' || simulationStatus === 'pending') && simulation && !isPublicView && !isAdminView) {
      const fetchQueueAndCalculateTime = async () => {
        try {
          // Fetch queue status
          const res = await fetch(`${API_URL}/simulations/queue-status`, { credentials: 'include' });
          const queueData = res.ok ? await res.json() : { queued_count: 0, worker_count: 1 };
          
          // Parse content to get persona/question counts
          let contentData: ContentData = {};
          if (simulation.content) {
            try {
              contentData = JSON.parse(simulation.content);
            } catch { /* ignore */ }
          }
          
          const numPersonas = simulation.personas?.length || 0;
          const numQuestions = contentData.num_questions || contentData.questions?.length || 0;
          const numImages = contentData.images?.length || 0;
          
          // Base simulation time: max(2, 2 * personas / 100) * (questions or images)
          // ~2 min per 100 personas, multiplied by number of items
          const items = numQuestions || numImages || 1;
          const perPersonaMinutes = Math.max(2, Math.ceil(2 * numPersonas / 100));
          const baseMinutes = perPersonaMinutes * items;
          
          // Queue wait time: estimate ~4 min avg per queued simulation, divided by workers
          let queueMinutes = 0;
          if (queueData.queued_count > 0) {
            const avgSimTime = 4; // avg 4 min per simulation in queue
            const workerCount = Math.max(1, queueData.worker_count);
            queueMinutes = Math.ceil((queueData.queued_count / workerCount) * avgSimTime);
          }
          
          const totalMinutes = baseMinutes + queueMinutes;
          
          setEstimatedTime({
            baseMinutes,
            queueMinutes,
            totalMinutes,
            queuedCount: queueData.queued_count
          });
          setRemainingMinutes(totalMinutes);
        } catch (e) {
          console.error('Failed to fetch queue status:', e);
          // Set default estimate
          setEstimatedTime({ baseMinutes: 2, queueMinutes: 0, totalMinutes: 2, queuedCount: 0 });
          setRemainingMinutes(2);
        }
      };
      
      fetchQueueAndCalculateTime();
    }
  }, [simulationStatus, simulation, isPublicView, isAdminView]);

  // Countdown timer for remaining time
  useEffect(() => {
    if (remainingMinutes !== null && remainingMinutes > 0 && (simulationStatus === 'running' || simulationStatus === 'pending')) {
      const timer = setInterval(() => {
        setRemainingMinutes(prev => (prev !== null && prev > 0) ? prev - 1 : 0);
      }, 60000); // Decrement every minute
      
      return () => clearInterval(timer);
    }
  }, [remainingMinutes, simulationStatus]);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside both the dropdown and the button
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target as Node);
      const isOutsideButton = dropdownButtonRef.current && !dropdownButtonRef.current.contains(event.target as Node);
      
      if (isOutsideDropdown && isOutsideButton) {
        setIsDetailsDropdownOpen(false);
      }
    };

    if (isDetailsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDetailsDropdownOpen]);

  // Click outside handler for share dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
        setShareDropdownOpen(false);
      }
    };

    if (shareDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shareDropdownOpen]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDetailsDropdownOpen) {
        setIsDetailsDropdownOpen(false);
      }
      if (event.key === 'Escape' && shareDropdownOpen) {
        setShareDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDetailsDropdownOpen, shareDropdownOpen]);

  const fetchSimulationStatus = async (initialFetch = false) => {
    if (!simulationId) return;

    try {
      // Use different endpoint based on view type
      let endpoint: string;
      if (isPublicView) {
        endpoint = `${API_URL}/simulations/public/${simulationId}`;
      } else if (isAdminView) {
        endpoint = `${API_URL}/admin/free-tier/simulations/${simulationId}`;
      } else {
        endpoint = `${API_URL}/simulations/${simulationId}`;
      }

      const response = await fetch(endpoint, {
        credentials: isPublicView ? 'omit' : 'include'  // No cookies for public
      });

      if (!response.ok) {
        throw new Error('Failed to fetch simulation status');
      }

      const data = await response.json();
      setSimulation(data);

      // Track if simulation is public (for showing share/unshare button) - not for admin view
      if (!isPublicView && !isAdminView && data.is_public) {
        setIsPublicShared(true);
        // Build share URL for modal
        const frontendUrl = window.location.origin;
        setShareUrl(`${frontendUrl}/simulation/public/${data.share_token}`);
      }

      if (data.simulation_response) {
        const hasOptimizationResponse = data.optimization_response && data.optimization_response.trim() !== '';

        if (hasOptimizationResponse) {
          setSimulationStatus('completed');
          setLoading(false);
        } else {
          setSimulationStatus('partial');
          setLoading(false);
        }
      } else {
        setSimulationStatus('running');
        setCurrentStep(getSimulationStep(data.status));
      }
    } catch (err) {
      console.error('Error fetching simulation status:', err);

      // Better error message based on view type
      if (isPublicView) {
        setError('This simulation is no longer publicly available. The owner may have made it private.');
      } else if (isAdminView) {
        setError('Failed to load simulation. The simulation may not exist.');
      } else {
        setError('Failed to load simulation status. Please try again.');
      }

      setLoading(false);
    }
  };

  const getSimulationStep = (status: string): string => {
    switch (status) {
      case 'initializing':
        return 'Initializing simulation...';
      case 'segment_processing':
        return 'Processing audience segments...';
      case 'generating_personas':
        return 'Generating audience profiles...';
      case 'running_simulation':
        return 'Running simulation with profiles...';
      case 'analyzing_results':
        return 'Analyzing simulation results...';
      case 'optimizing':
        return 'Optimizing recommendations...';
      case 'complete':
        return 'Simulation complete!';
      default:
        return 'Processing...';
    }
  };

  const parseContentField = (): ContentData => {
    if (!simulation?.content) {
      return {};
    }

    try {
      const parsed = JSON.parse(simulation.content) as ContentData;
      return parsed;
    } catch (error) {
      console.error('Error parsing content field:', error);
      return {};
    }
  };

  const extractPersonaInfo = (personaData: string | Record<string, unknown>) => {
    try {
      const data = typeof personaData === 'string' ? JSON.parse(personaData) : personaData;
      const extractField = (field: string, defaultValue: string = 'N/A'): string => {
        const value = data[field];
        if (value === undefined || value === null) return defaultValue;
        if (typeof value !== 'object') return String(value);
        try {
          if (Object.keys(value as Record<string, unknown>).length > 5) {
            return `Complex data (${Object.keys(value as Record<string, unknown>).length} properties)`;
          }
          return JSON.stringify(value, null, 2);
        } catch (error) {
          return 'Complex data';
        }
      };

      return {
        name: extractField('name', 'Unknown'),
        age: extractField('age'),
        job_title: extractField('job_title'),
        four_word_description: extractField('four_word_description'),
        occupation: extractField('occupation'),
        behavioral_archetype: extractField('behavioral_archetype'),
        organizational_influence: extractField('organizational_influence'),
      };
    } catch (error) {
      console.error('Error parsing persona data:', error);
      return {
        name: 'Unknown',
        age: 'N/A',
        job_title: 'N/A',
        four_word_description: 'N/A',
        occupation: 'N/A',
        behavioral_archetype: 'N/A',
        organizational_influence: 'N/A'
      };
    }
  };

  // Media handling functions
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    const validFiles: File[] = [];
    const previewUrls: string[] = [];
    
    Array.from(files).forEach(file => {
      if (file.size > 20 * 1024 * 1024) {
        setNotification({message: `File ${file.name} exceeds 20MB limit`, type: 'error'});
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        setNotification({message: `File ${file.name} is not supported`, type: 'error'});
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      if (mediaFiles.length + validFiles.length >= 4) {
        setNotification({message: 'Maximum 4 files allowed', type: 'error'});
        setTimeout(() => setNotification(null), 3000);
        return;
      }
      
      validFiles.push(file);
      previewUrls.push(URL.createObjectURL(file));
    });
    
    if (validFiles.length > 0) {
      setMediaFiles(prev => [...prev, ...validFiles]);
      setMediaPreviewUrls(prev => [...prev, ...previewUrls]);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSendMessage = async () => {
    if (!simulationId || (!chatMessage.trim() && mediaFiles.length === 0) || chatState.isTyping || chatState.isSending || isUploadingMedia) return;

    const message = chatMessage.trim();
    let optimisticMessageId: string | null = null;

    try {
      setChatMessage('');
      
      if (mediaFiles.length > 0) {
        setIsUploadingMedia(true);
      }

      let mediaData: any = {};
      if (mediaFiles.length > 0) {
        const images: string[] = [];
        const videos: string[] = [];
        
        for (const file of mediaFiles) {
          const base64Data = await convertFileToBase64(file);
          
          if (file.type.startsWith('image/')) {
            images.push(base64Data);
          } else if (file.type.startsWith('video/')) {
            videos.push(base64Data);
          }
        }
        
        if (images.length > 0) mediaData.images = images;
        if (videos.length > 0) mediaData.videos = videos;
      }

      const tempMediaFiles = [...mediaFiles];
      const tempPreviewUrls = [...mediaPreviewUrls];

      let chatType: 'persona' | 'simulation';
      let options: any = {};

      if (chatTab === 'simulation') {
        chatType = 'simulation';
        options = { 
          simulationId: parseInt(String(simulationId)),
          ...mediaData
        };
      } else if (chatTab === 'persona' && selectedPersona) {
        chatType = 'persona';
        options = { 
          simulationId: parseInt(String(simulationId)), 
          personaId: selectedPersona,
          ...mediaData
        };
      } else {
        throw new Error('Invalid chat target');
      }

      const messageMedia = tempMediaFiles.length > 0 ? {
        images: mediaData.images || [],
        videos: mediaData.videos || []
      } : undefined;
      
      optimisticMessageId = addOptimisticMessage(
        message || 'Please analyze the attached media', 
        chatType,
        messageMedia
      );

      await sendChatMessage(message || 'Please analyze the attached media', chatType, options);
      
      setMediaFiles([]);
      setMediaPreviewUrls([]);
      
      tempPreviewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    } catch (err) {
      console.error('Error sending chat message:', err);
      setError(`Failed to send message: ${err instanceof Error ? err.message : 'Unknown error'}`);
      
      if (optimisticMessageId) {
        removeOptimisticMessage(optimisticMessageId);
      }
      
      setChatMessage(message);
      setIsUploadingMedia(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Helper function to convert image URL to base64 (converts to JPEG/PNG for PDF compatibility)
  const convertImageToBase64 = async (url: string): Promise<string> => {
    try {
      // Check if this is a question-images URL - use the base64 endpoint to avoid CORS issues
      // Matches: /api/question-images/serve/{user_id}/{filename} or full URL with same path
      const questionImageMatch = url.match(/\/api\/question-images\/serve\/(\d+)\/([^?#]+)/);
      
      if (questionImageMatch) {
        const userId = questionImageMatch[1];
        const filename = questionImageMatch[2];
        const base64Url = `${API_URL}/api/question-images/base64/${userId}/${filename}`;
        
        console.log(`[PDF Export] Using base64 endpoint for question image: ${url} -> ${base64Url}`);
        
        const response = await fetch(base64Url, {
          credentials: 'include',
          signal: AbortSignal.timeout(30000)
        });
        
        if (!response.ok) {
          console.error(`[PDF Export] Failed to get base64 image: ${base64Url}, status: ${response.status}`);
          throw new Error(`Failed to get base64 image: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.data_url) {
          console.log(`[PDF Export] Successfully got base64 for question image: ${filename}`);
          return data.data_url;
        }
        
        throw new Error('No data_url in response');
      }
      
      // For other images, use the standard approach
      // Ensure we have an absolute URL
      let absoluteUrl: string;
      if (url.startsWith('http')) {
        absoluteUrl = url;
      } else if (url.startsWith('/api') || url.startsWith('/uploads') || url.startsWith('/static')) {
        // Backend API URLs need the API_URL prefix
        absoluteUrl = `${API_URL}${url}`;
      } else if (url.startsWith('/')) {
        // Other absolute paths use origin
        absoluteUrl = `${window.location.origin}${url}`;
      } else {
        // Relative paths use origin
        absoluteUrl = `${window.location.origin}/${url}`;
      }
      
      console.log(`[PDF Export] Converting image via fetch: ${url} -> ${absoluteUrl}`);
      
      // Fetch image with credentials
      const response = await fetch(absoluteUrl, { 
        credentials: 'include',
        // Add timeout for image fetching
        signal: AbortSignal.timeout(30000)
      });
      
      if (!response.ok) {
        console.error(`[PDF Export] Failed to fetch image: ${absoluteUrl}, status: ${response.status}`);
        throw new Error(`Failed to fetch image: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log(`[PDF Export] Fetched image blob: ${blob.type}, size: ${blob.size}`);
      
      // Convert to JPEG/PNG for wkhtmltopdf compatibility (doesn't support WebP well)
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          try {
            // Create canvas to convert image
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              throw new Error('Failed to get canvas context');
            }
            
            // Draw image on canvas
            ctx.drawImage(img, 0, 0);
            
            // Convert to JPEG (or PNG for images with transparency)
            // Use JPEG for better compression for photos, PNG for graphics/transparency
            const outputFormat = 'image/jpeg';
            const quality = 0.85; // Good quality with reasonable file size
            
            // Get base64 string
            const base64 = canvas.toDataURL(outputFormat, quality);
            console.log(`[PDF Export] Successfully converted image to base64: ${url}`);
            resolve(base64);
          } catch (err) {
            console.error('[PDF Export] Canvas conversion error:', err);
            reject(err);
          }
        };
        
        img.onerror = (e) => {
          console.error(`[PDF Export] Image element failed to load: ${url}`, e);
          reject(new Error('Failed to load image in Image element'));
        };
        
        // Convert blob to data URL for the image element
        const reader = new FileReader();
        reader.onloadend = () => {
          img.src = reader.result as string;
        };
        reader.onerror = (e) => {
          console.error('[PDF Export] FileReader error:', e);
          reject(e);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('[PDF Export] Failed to convert image:', url, error);
      // Return a transparent 1x1 pixel as fallback
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
    }
  };

  // Enhanced content preparation function for export
  const prepareContentForExport = async () => {
    try {
      // 1. Set export mode for specialized components
      setIsPreparingExport(true);
      
      // 2. Wait for export state to propagate and images to re-render
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // 3. Get the analysis section
      const analysisSection = document.getElementById('simulation-analysis-section');
      if (!analysisSection) {
        throw new Error('Analysis section not found');
      }
      
      // 4. Clone the section to avoid modifying the actual DOM
      const clonedSection = analysisSection.cloneNode(true) as HTMLElement;
      
      // 5. Convert all images to base64 (including images inside foreignObject elements)
      const images = clonedSection.querySelectorAll('img');
      console.log(`[PDF Export] Found ${images.length} images to convert to base64`);
      
      let successCount = 0;
      let failCount = 0;
      
      const imageConversions = Array.from(images).map(async (img) => {
        const src = img.getAttribute('src');
        if (src && !src.startsWith('data:')) {
          try {
            const base64 = await convertImageToBase64(src);
            img.setAttribute('src', base64);
            // Also remove loading="lazy" attribute that might cause issues
            img.removeAttribute('loading');
            successCount++;
          } catch (error) {
            console.error('[PDF Export] Failed to convert image:', src, error);
            // Set a placeholder image instead of leaving broken src
            img.setAttribute('src', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==');
            failCount++;
          }
        }
      });
      
      // Wait for all image conversions
      await Promise.all(imageConversions);
      console.log(`[PDF Export] Image conversion complete: ${successCount} success, ${failCount} failed`);
      
      // 6. Also handle background images in style attributes
      const elementsWithBackgroundImage = clonedSection.querySelectorAll('[style*="background-image"]');
      for (const el of elementsWithBackgroundImage) {
        const style = el.getAttribute('style') || '';
        const urlMatch = style.match(/url\(['"]?([^'")\s]+)['"]?\)/);
        if (urlMatch && urlMatch[1] && !urlMatch[1].startsWith('data:')) {
          try {
            const base64 = await convertImageToBase64(urlMatch[1]);
            const newStyle = style.replace(/url\(['"]?[^'")\s]+['"]?\)/, `url('${base64}')`);
            el.setAttribute('style', newStyle);
          } catch (error) {
            console.error('[PDF Export] Failed to convert background image:', urlMatch[1], error);
          }
        }
      }
      
      // 7. Remove no-print elements and buttons
      const noPrintElements = clonedSection.querySelectorAll('.no-print, button');
      noPrintElements.forEach(el => el.remove());
      
      // 8. Get the HTML content with embedded images
      const divHtml = clonedSection.outerHTML;
      
      return divHtml;
    } finally {
      // Reset export mode
      setIsPreparingExport(false);
    }
  };

  // PDF generation functions
  const startAsyncPdfGeneration = async (shouldSendEmail = false, emailRecipient = '') => {
    try {
      setPdfGenerating(true);
      setCachedPdfUrl(null);
      setPdfCacheKey(null);
      
      if (pdfCheckInterval) {
        clearInterval(pdfCheckInterval);
        setPdfCheckInterval(null);
      }
      
      // Use enhanced content preparation
      const divHtml = await prepareContentForExport();
      
      const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
        .map(node => {
          if (node instanceof HTMLLinkElement) {
            const absoluteUrl = new URL(node.href, window.location.origin).href;
            return `<link rel="stylesheet" href="${absoluteUrl}">`;
          }
          return node.outerHTML;
        })
        .join('\n');
      
      const customPrintStyle = `
      <style>
        .no-print { display: none !important; }
        button { display: none !important; }
        @media print {
          @page { margin: 0.75in; size: letter; }
          * { -webkit-print-color-adjust: exact !important; }
          body { font-family: 'Inter', sans-serif; line-height: 1.4; }
        }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 12px; text-align: left; }
        .text-primary { color: #07E5D1; }
        .bg-primary-light { background-color: #E8FCFA; }
      </style>
      `;
      
      const reportHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Simulation Analysis Report</title>
        ${styles}
        ${customPrintStyle}
      </head>
      <body style="background-color: white; padding: 20px;">
        ${divHtml}
      </body>
      </html>
      `;
      
      const response = await fetch(`${API_URL}/api/request-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          html: reportHtml, 
          simulationId: simulationId || 'simulation' 
        }),
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to initiate PDF generation');
      
      const data = await response.json();
      setPdfCacheKey(data.cache_key);
      
      if (data.message === 'PDF already available') {
        setPdfGenerating(false);
        const pdfUrl = `${API_URL}/api/get-pdf/${data.cache_key}`;
        setCachedPdfUrl(pdfUrl);
        
        if (shouldSendEmail) {
          await sendPdfByEmailInternal(data.cache_key, emailRecipient);
        } else {
          downloadPdf(pdfUrl);
        }
        return;
      }
      
      // Start polling for PDF status
      const interval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`${API_URL}/api/check-pdf-status/${simulationId || 'simulation'}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cache_key: data.cache_key }),
            credentials: 'include',
          });
          
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            if (statusData.ready) {
              clearInterval(interval);
              setPdfCheckInterval(null);
              setPdfGenerating(false);
              const pdfUrl = `${API_URL}${statusData.url}`;
              setCachedPdfUrl(pdfUrl);
              
              if (shouldSendEmail) {
                await sendPdfByEmailInternal(data.cache_key, emailRecipient);
              } else {
                downloadPdf(pdfUrl);
              }
            }
          }
        } catch (error) {
          console.error('Error checking PDF status:', error);
        }
      }, 2000);
      
      setPdfCheckInterval(interval);
      
      setTimeout(() => {
        if (interval) {
          clearInterval(interval);
          setPdfCheckInterval(null);
          setPdfGenerating(false);
          setExportError('PDF generation timed out. Please try again.');
        }
      }, 300000);
      
    } catch (error) {
      console.error('Error starting PDF generation:', error);
      setPdfGenerating(false);
      setExportError('Failed to generate PDF. Please try again.');
    }
  };
  
  const downloadPdf = (pdfUrl: string) => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    const timestamp = new Date().toISOString().split('T')[0];
    const simId = simulation?.id || 'unknown';
    link.download = `simulation-${simId}-${timestamp}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const sendPdfByEmailInternal = async (cacheKey: string, emailRecipient: string) => {
    try {
      const contentData = parseContentField();
      const simulationName = contentData.name || `Simulation ${simulationId}`;
      
      const res = await fetch(`${API_URL}/send-pdf-by-cache-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cache_key: cacheKey,
          to: emailRecipient,
          subject: `${simulationName} - Analysis PDF`,
          simulation_name: simulationName,
        }),
        credentials: 'include',
        signal: AbortSignal.timeout(60000)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send email');
      }
      setExportEmailStatus('success');
      
    } catch (err: any) {
      setExportEmailStatus('error');
      setExportEmailError(err.message || 'Failed to send email');
    }
  };
  
  const sendPdfByEmail = async (email: string) => {
    if (!email) return;
    
    setCurrentEmailRecipient(email);
    setExportEmailStatus('sending');
    setExportEmailError('');
    
    await startAsyncPdfGeneration(true, email);
  };

  const handleEmailNotification = async (email: string) => {
    if (!simulationId) return;
    setEmailNotificationStatus('sending');
    setEmailNotificationError('');
    try {
      const response = await fetch(`${API_URL}/monitor-simulation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          simulationId: simulationId,
          email: email
        })
      });
      if (!response.ok) {
        throw new Error('Failed to set up email notification');
      }
      setEmailNotificationStatus('success');
      setShowEmailModal(false);
      setNotificationEmail(email);
    } catch (err) {
      console.error('Error setting up email notification:', err);
      setEmailNotificationStatus('error');
      setEmailNotificationError('Failed to set up email notification. Please try again.');
    }
  };

  const renderLoadingAnimation = () => {
    const isSimulationRunning = !simulation?.simulation_response;
    const isRendering = !!simulation?.simulation_response && loading;

    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="animate-spin mb-6">
          <Loader2 className="h-12 w-12 text-primary" />
        </div>
        <h3 className="text-xl font-semibold mb-2">
          {isSimulationRunning
            ? (simulationStatus === 'pending' ? 'Starting simulation...' : currentStep)
            : isRendering
              ? 'Rendering simulation...'
              : currentStep}
        </h3>
        <p className="text-gray-500 text-center max-w-md mb-4">
          {isSimulationRunning
            ? 'Please wait while we run your simulation.'
            : isRendering
              ? 'Rendering your simulation results. This may take a few moments.'
              : ''}
        </p>
        {/* Estimated Time Display */}
        {isSimulationRunning && estimatedTime && (
          <div className="text-sm text-gray-600 mb-6 text-center">
            <div className="font-medium text-gray-800">
              Estimated time: ~{remainingMinutes !== null ? remainingMinutes : estimatedTime.totalMinutes} min
            </div>
            {estimatedTime.queueMinutes > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                ({estimatedTime.baseMinutes} min processing + {estimatedTime.queueMinutes} min queue wait)
              </div>
            )}
            {estimatedTime.queuedCount > 0 && (
              <div className="text-xs text-gray-400 mt-1">
                {estimatedTime.queuedCount} simulation{estimatedTime.queuedCount > 1 ? 's' : ''} ahead in queue
              </div>
            )}
          </div>
        )}
        {isSimulationRunning && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-md w-full">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900 mb-1">
                  Get notified when ready
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  We'll email you once your simulation is complete so you can step away.
                </p>
                <button
                  className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2 px-4 rounded transition-colors disabled:opacity-60"
                  onClick={() => setShowEmailModal(true)}
                  disabled={emailNotificationStatus === 'sending'}
                >
                  {emailNotificationStatus === 'sending' ? 'Setting up...' : 'Get Email Notification'}
                </button>
                {emailNotificationStatus === 'success' && (
                  <div className="mt-2 text-sm text-primary font-medium">
                    ✓ Email notification set up successfully!
                  </div>
                )}
                {emailNotificationStatus === 'error' && (
                  <div className="mt-2 text-sm text-red-500">
                    {emailNotificationError}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const contentData = parseContentField();

  // Handle error state for public/admin view - return early
  if (error && (isPublicView || isAdminView)) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {isAdminView ? 'Simulation Not Found' : 'Simulation Not Available'}
          </h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-500 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {loading && !simulation ? (
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <div className="animate-spin mb-6">
            <Loader2 className="h-12 w-12 text-primary" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Loading simulation...</h3>
          <p className="text-gray-500 text-center max-w-md">Please wait</p>
        </div>
      ) : loading && simulation && !simulation.simulation_response ? (
        renderLoadingAnimation()
      ) : loading && simulation && simulation.simulation_response ? (
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <div className="animate-spin mb-6">
            <Loader2 className="h-12 w-12 text-primary" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Loading simulation...</h3>
          <p className="text-gray-500 text-center max-w-md">Please wait</p>
        </div>
      ) : (
        <div className="flex h-full" style={{ height: "80vh" }}>
          {/* Chat Interface - Only in private view (not public or admin) */}
          {!isPublicView && !isAdminView && (
            <div className={`bg-white rounded-xl shadow-sm h-full relative transition-all duration-300 ease-in-out ${isChatCollapsed ? 'w-0 opacity-0' : 'w-1/3 opacity-100'}`} style={{ overflow: isChatCollapsed ? 'hidden' : 'visible' }}>
              <div className={`h-full transition-opacity duration-300 ease-in-out ${isChatCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                <ChatInterface
                  chatTab={chatTab}
                  chatState={chatState}
                  chatMessage={chatMessage}
                  mediaFiles={mediaFiles}
                  mediaPreviewUrls={mediaPreviewUrls}
                  selectedPersona={selectedPersona}
                  simulation={simulation}
                  isChatConnected={isChatConnected}
                  isUploadingMedia={isUploadingMedia}
                  isPreparingExport={isPreparingExport}
                  onChatTabChange={handleChatTabChange}
                  onChatMessageChange={setChatMessage}
                  onSendMessage={handleSendMessage}
                  onKeyDown={handleKeyDown}
                  onFileSelect={handleFileSelect}
                  onRemoveMedia={handleRemoveMedia}
                  onUploadClick={handleUploadClick}
                  onViewMedia={handleViewMedia}
                  fileInputRef={fileInputRef}
                  extractPersonaInfo={extractPersonaInfo}
                />
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className={`bg-white rounded-xl shadow-sm overflow-hidden h-full flex flex-col transition-all duration-300 ease-in-out ${(isPublicView || isAdminView) ? 'w-full' : (isChatCollapsed ? 'w-[calc(100%-3rem)]' : 'w-2/3')}`}>
            {/* Header */}
            <div className="border-b border-gray-200 bg-white px-6 py-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  {/* Only show chat toggle in private view (not public or admin) */}
                  {!isPublicView && !isAdminView && (
                    <button
                      onClick={handleToggleChat}
                      onMouseEnter={() => setShowHoverMessage(true)}
                      onMouseLeave={() => setShowHoverMessage(false)}
                      className="relative flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors mr-8"
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span>Chat</span>
                      {showHoverMessage && (
                        <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                          {isChatCollapsed ? 'Click to open chat' : 'Click to collapse chat'}
                        </div>
                      )}
                    </button>
                  )}
                  <nav className="flex gap-4" aria-label="Tabs">
                    <button
                      onClick={() => handleActiveTabChange('simulation')}
                      className={`py-2 inline-flex items-center border-b-2 text-sm font-medium ${
                        activeTab === 'simulation'
                          ? 'border-primary text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <BarChart2 className="mr-2 h-4 w-4" />
                      Simulation Analysis
                    </button>
                  </nav>
                </div>
                {!isPublicView && !isAdminView && (
                  <div className="flex items-center space-x-2">
                    {/* Share/Unshare Button */}
                    {!isPublicShared ? (
                      <div className="relative">
                        <button
                          onClick={handleShare}
                          disabled={isSharing}
                          onMouseEnter={() => setShowTooltip('share')}
                          onMouseLeave={() => setShowTooltip('')}
                          className="p-2 rounded-md border border-primary transition-colors disabled:opacity-50 text-primary hover:bg-primary-light h-9 w-9"
                        >
                          {isSharing ? (
                            <RefreshCw className="w-5 h-5 animate-spin" />
                          ) : (
                            <Share2 className="w-5 h-5" />
                          )}
                        </button>
                        {showTooltip === 'share' && (
                          <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                            Share simulation
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="relative" ref={shareDropdownRef}>
                        <button
                          onClick={() => setShareDropdownOpen(!shareDropdownOpen)}
                          onMouseEnter={() => setShowTooltip('shareManage')}
                          onMouseLeave={() => setShowTooltip('')}
                          className="flex items-center gap-1 p-2 rounded-md bg-gray-50 border border-primary hover:bg-gray-100 transition-colors text-primary h-9"
                        >
                          <Share2 className="w-5 h-5" />
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        {showTooltip === 'shareManage' && !shareDropdownOpen && (
                          <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                            Manage sharing
                          </div>
                        )}

                        {shareDropdownOpen && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                            <button
                              onClick={() => {
                                setShareDropdownOpen(false);
                                setIsShareModalOpen(true);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <ExternalLink className="w-4 h-4" />
                              View Share Link
                            </button>
                            <button
                              onClick={() => {
                                setShareDropdownOpen(false);
                                handleUnshare();
                              }}
                              disabled={isUnsharing}
                              className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                            >
                              <ShieldOff className="w-4 h-4" />
                              {isUnsharing ? 'Making Private...' : 'Make Private'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Copy Button */}
                  <div className="relative">
                    <button
                      onMouseEnter={() => setShowTooltip('copy')}
                      onMouseLeave={() => setShowTooltip('')}
                      onClick={handleCopyToClipboard}
                      className={`p-2 rounded-md border transition-colors focus:outline-none focus:ring-2 h-9 w-9 ${
                        copied ? 'text-primary bg-primary-light border-primary/20 focus:ring-primary' : 'text-primary border-primary hover:bg-primary-light focus:ring-primary'
                      }`}
                      title="Copy simulation output"
                      aria-label="Copy simulation output"
                    >
                      <Copy className="h-5 w-5" />
                    </button>
                    {showTooltip === 'copy' && (
                      <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                        Click to copy output
                      </div>
                    )}
                  </div>
                  {copied && (
                    <span className="text-primary text-xs ml-1 transition-opacity duration-200">Copied!</span>
                  )}
                  
                  {/* Export Button */}
                  <div className="relative">
                    <button
                      onMouseEnter={() => setShowTooltip('export')}
                      onMouseLeave={() => setShowTooltip('')}
                      onClick={handleOpenExportModal}
                      className="p-2 rounded-md border border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-primary hover:bg-primary-light h-9 w-9"
                      title="Export simulation analysis"
                      aria-label="Export simulation analysis"
                    >
                      <Download className="h-5 w-5" />
                    </button>
                    {showTooltip === 'export' && (
                      <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                        Click to export as PDF
                      </div>
                    )}
                  </div>
                  
                  {/* Edit & Rerun Button */}
                  <div className="relative">
                    <button
                      onMouseEnter={() => setShowTooltip('editRerun')}
                      onMouseLeave={() => setShowTooltip('')}
                      onClick={() => setShowEditRerunModal(true)}
                      className="p-2 rounded-md border border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-primary hover:bg-primary-light h-9 w-9"
                      title="Edit and rerun simulation"
                      aria-label="Edit and rerun simulation"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {showTooltip === 'editRerun' && (
                      <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                        Edit inputs and rerun
                      </div>
                    )}
                  </div>

                  {/* Rerun Button */}
                  <div className="relative">
                    <button
                      onMouseEnter={() => setShowTooltip('rerun')}
                      onMouseLeave={() => setShowTooltip('')}
                      onClick={handleRerunSimulation}
                      className="p-2 rounded-md border border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-primary hover:bg-primary-light disabled:opacity-50 h-9 w-9"
                      disabled={rerunLoading}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    {showTooltip === 'rerun' && (
                      <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                        Click to rerun simulation
                      </div>
                    )}
                  </div>
                  
                  {/* Simulation Inputs Button */}
                  <div className="relative">
                    <button
                      ref={dropdownButtonRef}
                      onMouseEnter={() => setShowTooltip('inputs')}
                      onMouseLeave={() => setShowTooltip('')}
                      onClick={handleToggleDetailsDropdown}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md border border-primary transition-colors text-primary hover:bg-primary-light h-9"
                    >
                      <FileText className="h-5 w-5" />
                      <span className="text-sm">Simulation Inputs</span>
                      {isDetailsDropdownOpen ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </button>
                    {showTooltip === 'inputs' && (
                      <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                        Click to view simulation inputs
                      </div>
                    )}
                  </div>
                  {rerunError && <span className="text-red-500 text-xs ml-2">{rerunError}</span>}
                </div>
                )}

                {/* Read-only banner for public and admin views */}
                {isPublicView && (
                  <div className="mb-4 px-3 py-2 bg-gray-50 border-l-4 border-gray-400 rounded">
                    <p className="text-xs text-gray-600">
                      Shared view - editing disabled
                    </p>
                  </div>
                )}
                {isAdminView && (
                  <div className="mb-4 px-3 py-2 bg-blue-50 border-l-4 border-blue-400 rounded">
                    <p className="text-xs text-blue-600">
                      Admin view - read only
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Main Analysis Panel */}
            <div className="flex-grow overflow-hidden relative">
              {simulation?.simulation_response && (
                <SimulationAnalysisPanel
                  simulation={simulation}
                  simulationResponse={simulation.simulation_response}
                  contentData={contentData}
                  isPreparingExport={isPreparingExport}
                  expandedCards={expandedCards}
                  onToggleCard={toggleCard}
                  isDetailsDropdownOpen={isDetailsDropdownOpen}
                  onCloseDropdown={() => setIsDetailsDropdownOpen(false)}
                  dropdownRef={dropdownRef}
                  onImageClick={handleImageClick}
                />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={handleCloseExportModal}
        pdfGenerating={pdfGenerating}
        onExportPDF={() => startAsyncPdfGeneration(false)}
        onSendEmail={sendPdfByEmail}
        exportEmailStatus={exportEmailStatus}
        exportEmailError={exportEmailError}
        exportError={exportError}
      />

      {/* Edit & Rerun Modal */}
      {simulation && (
        <EditRerunModal
          isOpen={showEditRerunModal}
          onClose={() => setShowEditRerunModal(false)}
          simulation={simulation}
          contentData={contentData}
        />
      )}

      {/* Share Modal */}
      {shareUrl && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareUrl={shareUrl}
          onUnshare={handleUnshare}
          isUnsharing={isUnsharing}
        />
      )}

      {/* Email Notification Modal */}
      {showEmailModal && (
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent className="sm:max-w-md">
            <CustomDialogClose onClick={() => setShowEmailModal(false)}>
              <X className="h-5 w-5" />
            </CustomDialogClose>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Mail className="h-5 w-5 mr-2 text-gray-600" />
                Email Notification
              </DialogTitle>
              <DialogDescription>
                Enter your email address to receive a notification when your simulation is complete.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label htmlFor="notification-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="notification-email"
                  type="email"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded"
                  onClick={() => setShowEmailModal(false)}
                  disabled={emailNotificationStatus === 'sending'}
                >
                  Cancel
                </button>
                <button
                  className="bg-primary hover:bg-primary-hover text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                  onClick={() => handleEmailNotification(notificationEmail)}
                  disabled={!notificationEmail || emailNotificationStatus === 'sending'}
                >
                  {emailNotificationStatus === 'sending' ? 'Setting up...' : 'Set Notification'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Media Viewer Modal */}
      {viewerMedia && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90">
          <button
            onClick={handleCloseViewerMedia}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            type="button"
            aria-label="Close viewer"
          >
            <X className="h-6 w-6" />
          </button>
          
          <div className="max-w-[90vw] max-h-[90vh] flex items-center justify-center">
            {viewerMedia.type === 'image' ? (
              <img 
                src={viewerMedia.src} 
                alt="Full size preview"
                className="max-w-full max-h-[90vh] object-contain rounded"
              />
            ) : (
              <video 
                src={viewerMedia.src}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-[90vh] rounded bg-black"
                onError={(e) => {
                  console.error('Video playback error in modal:', e);
                  setNotification({message: 'Unable to play video', type: 'error'});
                  setTimeout(() => setNotification(null), 3000);
                }}
              />
            )}
          </div>
          
          <div 
            className="absolute inset-0 -z-10"
            onClick={handleCloseViewerMedia}
          />
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-75"
          onClick={handleCloseImageModal}
        >
          <div className="relative p-4 bg-white rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.alt} 
              className="max-w-screen-md max-h-[80vh] object-contain"
            />
            <button
              onClick={handleCloseImageModal}
              className="absolute -top-3 -right-3 text-white bg-gray-800 rounded-full h-8 w-8 flex items-center justify-center text-lg font-bold hover:bg-black"
              aria-label="Close image popup"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed bottom-4 right-4 ${zIndexClasses.toast} animate-fade-in`}>
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            notification.type === 'success'
              ? 'bg-primary text-white'
              : 'bg-red-500 text-white'
          }`}>
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <>
          <div
            className={`fixed inset-0 bg-black bg-opacity-50 ${zIndexClasses.maximum}`}
            onClick={() => setConfirmDialog(null)}
          />
          <div className={`fixed inset-0 flex items-center justify-center ${zIndexClasses.maximum} px-4`}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Confirm Action</h3>
              <p className="text-sm text-gray-600 mb-6">{confirmDialog.message}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDialog.onConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
});

SimulationResultsContent.displayName = 'SimulationResultsContent';

export default SimulationResultsContent;

